import { useState } from 'react';
import { toast } from 'sonner';
import { RefreshCwIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnGhost } from '../ui/styles';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchIntegrations, refreshIntegration } from '../adminApi';
import { integrationInfo } from '../../data/insightIntegrations';
import { errorMessage } from '../../core/errors';
import { dayShort } from '../../core/format/dates';
import type { IntegrationId, IntegrationStatus } from '../../core/types/insights';
const order: IntegrationId[] = ['umami', 'searchConsole', 'googleBusiness', 'instagram', 'brevo'];
const badge = (s: IntegrationStatus) => s.status === 'gekoppeld' ? {
  tone: 'success' as const,
  label: 'Gekoppeld'
} : s.status === 'fout' ? {
  tone: 'soft' as const,
  label: 'Ophalen mislukt'
} : {
  tone: 'neutral' as const,
  label: 'Niet gekoppeld'
};

/**
 * Instellingen › Koppelingen & privacy › Koppelingen: per externe bron de status, wat hij oplevert en hoe je hem koppelt.
 * Koppelen zelf gebeurt op de server; hier staan nooit sleutels of wachtwoorden (docs/INZICHT.md §7).
 */
export function IntegrationSettings() {
  const {
    data,
    setData,
    status,
    reload
  } = useLatestLoad(fetchIntegrations);
  const [busy, setBusy] = useState<IntegrationId | null>(null);
  const refresh = async (id: IntegrationId) => {
    setBusy(id);
    try {
      const next = await refreshIntegration(id);
      setData(d => d ? {
        ...d,
        [id]: next
      } : d);
      toast.success(`${integrationInfo[id].name}: ophalen staat klaar`);
    } catch (err) {
      toast.error('Opnieuw ophalen lukte niet', {
        description: errorMessage(err)
      });
    } finally {
      setBusy(null);
    }
  };
  return <Panel title="Koppelingen" description="Waar Inzicht zijn cijfers vandaan haalt. De server haalt elke nacht op; koppelen gebeurt door wie de server beheert, met de stappen hieronder." flush data-id="element-4219" data-mp="2x7b00q6kvbph">
      {!data ? status === 'error' ? <LoadFailed variant="inline" title="De status van de koppelingen kon niet laden" onRetry={reload} className="px-4 py-6 sm:px-5" data-id="element-4220" data-mp="27idxv7ctl7xk" /> : <LoadingLine className="px-4 py-6 sm:px-5" data-id="element-4221" data-mp="2mebo6902bi1v" /> : <ul className="divide-y divide-line" data-id="element-4222" data-mp="2hga9s1dl06wq">
          {order.map(id => {
        const s = data[id];
        const info = integrationInfo[id];
        const b = badge(s);
        return <li key={id} className="px-4 py-4 sm:px-5" data-id="element-4223" data-mp="1osxkhjg1dkzq">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-4224" data-mp="23gsfj6hio1fz">
                  <span className="text-[14px] text-ink" data-id="element-4225" data-mp="23ivbs899iqt5">{info.name}</span>
                  <Badge tone={b.tone} data-id="element-4226" data-mp="1yohgs6yiilp0">{b.label}</Badge>
                  {s.status !== 'niet-gekoppeld' && <button type="button" onClick={() => void refresh(id)} disabled={busy !== null} className={`${btnGhost} ml-auto disabled:opacity-50`} data-id="element-4227" data-mp="1ayn69w1o197x">
                      <RefreshCwIcon className={`h-3.5 w-3.5 ${busy === id ? 'animate-spin' : ''}`} strokeWidth={1.75} aria-hidden="true" data-id="element-4228" data-mp="3hogmmrln3xuh" />
                      {busy === id ? 'Ophalen…' : 'Opnieuw ophalen'}
                    </button>}
                </div>
                <p className="mt-1 text-[13px] text-muted" data-id="element-4229" data-mp="3cpdta4f0vg2c">{info.what}</p>
                {s.updatedAt && <p className="mt-1 text-[12px] text-muted" data-id="element-4230" data-mp="49c0nzwe73b5">
                    Bijgewerkt tot {dayShort(s.updatedAt.slice(0, 10))}, {s.updatedAt.slice(11, 16)}
                  </p>}
                {s.message && <p className={`mt-1 text-[12px] ${s.status === 'fout' ? 'text-clay' : 'text-muted'}`} data-id="element-4231" data-mp="3rdi2mhs6qf8g">{s.message}</p>}
                {s.status !== 'gekoppeld' && <ol className="mt-3 list-decimal space-y-1 pl-5 text-[13px] text-ink" data-id="element-4232" data-mp="2fzedbowjlf9q">
                    {info.steps.map(step => <li key={step} data-id="element-4233" data-mp="2k219g208xmcq">{step}</li>)}
                  </ol>}
              </li>;
      })}
        </ul>}
    </Panel>;
}